import React, { useEffect, useState } from 'react';
import $ from "jquery";
import { defaultPdf, defaultVideo, docsImage, uploadImage } from '../../constants/images';

const checkDimensions = (file, options) => {
    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            const ratio = width / height;

            if (options.minWidth && width < options.minWidth) {
                resolve({
                    valid: false,
                    message: options.maxWidth
                        ? `Image width (${width} px) is too small. Allowed width: ${options.minWidth} - ${options.maxWidth} px.`
                        : `Image width (${width} px) is too small. Minimum width: ${options.minWidth} px.`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            if (options.maxWidth && width > options.maxWidth) {
                resolve({
                    valid: false,
                    message: options.minWidth
                        ? `Image width (${width} px) is too large. Allowed width: ${options.minWidth} - ${options.maxWidth} px.`
                        : `Image width (${width} px) is too large. Maximum width: ${options.maxWidth} px.`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            if (options.minHeight && height < options.minHeight) {
                resolve({
                    valid: false,
                    message: options.maxHeight
                        ? `Image height (${height} px) is too low. Allowed height: ${options.minHeight} - ${options.maxHeight} px.`
                        : `Image height (${height} px) is too low. Minimum height: ${options.minHeight} px.`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            if (options.maxHeight && height > options.maxHeight) {
                resolve({
                    valid: false,
                    message: options.minHeight
                        ? `Image height (${height} px) is too high. Allowed height: ${options.minHeight} - ${options.maxHeight} px.`
                        : `Image height (${height} px) is too high. Maximum height: ${options.maxHeight} px.`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            if (options.minAspectRatio && ratio < options.minAspectRatio) {
                resolve({
                    valid: false,
                    message: options.customRatioMessage || `Image is too tall (${width}×${height} px, ratio ${ratio.toFixed(2)}:1). Please upload a wide banner image with high width and low height (e.g. 1200×480 px, ratio ${options.minAspectRatio}:1 or wider).`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            if (options.maxAspectRatio && ratio > options.maxAspectRatio) {
                resolve({
                    valid: false,
                    message: `Image is excessively wide (${width}×${height} px, ratio ${ratio.toFixed(2)}:1). Recommended wide ratio is approx 2.2:1 - 2.6:1 (e.g. 1200×500 px).`,
                    width,
                    height,
                    ratio,
                });
                return;
            }

            resolve({ valid: true, width, height, ratio });
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ valid: false, message: "Failed to read image file." });
        };
        img.src = objectUrl;
    });
};

const ImageUpload = ({
    title = "",
    inputId = "",
    src = "",
    alt = "",
    imgId = "",
    type = "upload",
    name = "",
    setValue = () => { },
    onUpload,
    register,
    isLoading,
    setLoading,
    accept = "image/*",
    name2 = "",
    validateDimensions,
    helpText,
    landscape,
    aspectRatio,
    width,
    height,
}) => {
    const [dimensionError, setDimensionError] = useState("");
    const [dimensionInfo, setDimensionInfo] = useState(null);

    const isLandscape = Boolean(
        landscape ||
        (validateDimensions && (validateDimensions.minAspectRatio ? validateDimensions.minAspectRatio >= 1.2 : true))
    );

    const boxWidth = width || (isLandscape ? "380px" : "140px");
    const boxHeight = height || (isLandscape ? "160px" : "140px");
    const boxRatio = aspectRatio || (isLandscape ? "21 / 9" : "1 / 1");

    useEffect(() => {
        const inputElem = $(`#${inputId}`);
        inputElem.off("change.imgupload").on("change.imgupload", async function () {
            const file = this.files?.[0];
            if (!file) return;

            if (validateDimensions && file.type?.startsWith("image/")) {
                const check = await checkDimensions(file, validateDimensions);
                if (!check.valid) {
                    setDimensionError(check.message);
                    setDimensionInfo(null);
                    this.value = "";
                    return;
                }
                setDimensionError("");
                setDimensionInfo(`${check.width} × ${check.height} px (Ratio ${check.ratio.toFixed(2)}:1)`);
            } else {
                setDimensionError("");
                setDimensionInfo(null);
            }

            var curElement = $(`#${imgId}`);
            var reader = new FileReader();
            reader.onload = function (e) {
                if (e?.target?.result) {
                    if (e.target.result.indexOf("/pdf") > -1 || e.target.result.indexOf("application/pdf") > -1) {
                        curElement.attr("src", defaultPdf);
                        if (name2) {
                            setValue(name2, 'pdf');
                        }
                    }
                    else if (e.target.result.indexOf("video/") > -1) {
                        curElement.attr("src", defaultVideo);
                        if (name2) {
                            setValue(name2, 'video');
                        }
                    }
                    else if (e.target.result.indexOf("application/msword") > -1 || e.target.result.indexOf("application/vnd.openxmlformats-officedocument.wordprocessingml.document") > -1) {
                        curElement.attr("src", docsImage);
                        if (name2) {
                            setValue(name2, 'doc');
                        }
                    } else {
                        curElement.attr("src", e.target.result);
                        if (name2) {
                            setValue(name2, 'image');
                        }
                    }
                }
            };

            reader.readAsDataURL(file);
        });

        return () => {
            inputElem.off("change.imgupload");
        };
    }, [inputId, imgId, name2, setValue, validateDimensions]);

    return (
        <div className="image-upload-wrapper" style={{ display: "inline-block", width: "fit-content", maxWidth: "100%" }}>
            <label
                htmlFor={inputId}
                style={{
                    cursor: "pointer",
                    display: "inline-block",
                    width: "fit-content",
                    maxWidth: "100%",
                    margin: 0,
                }}
            >
                <img
                    className='image-upload pointer-footer'
                    id={imgId}
                    src={src || uploadImage}
                    alt={alt}
                    style={{
                        objectFit: src ? "cover" : "contain",
                        width: boxWidth,
                        maxWidth: "100%",
                        height: boxHeight,
                        aspectRatio: boxRatio,
                        border: dimensionError ? "1px solid #ea5455" : "1px dashed #7367f0",
                        padding: src ? "0px" : (isLandscape ? "30px" : "10px"),
                        backgroundColor: "#1b2438",
                        borderRadius: "12px",
                        display: "block",
                    }}
                />
                {title}
            </label>
            <input
                type="file"
                className="form-control image-upload-input pointer-footer"
                id={inputId}
                name={name}
                onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    if (validateDimensions && file.type?.startsWith("image/")) {
                        const check = await checkDimensions(file, validateDimensions);
                        if (!check.valid) {
                            e.target.value = "";
                            return;
                        }
                    }

                    try {
                        const formData = new FormData();
                        formData.append("media", file);
                        const res = await fetch("https://www.unigoal.com:7109/v2/admin/uploadFile", {
                            method: "POST",
                            body: formData
                        });
                        const data = await res.json();
                        const url = data?.result?.media_url || "";
                        setValue(name, url || "");
                        if (onUpload) {
                            onUpload(url || "");
                        }
                    } catch (err) {
                        console.log("upload error", err);
                        setValue(name, "");
                        if (onUpload) {
                            onUpload("");
                        }
                    }
                }}
                accept={accept}
            />
            {helpText && (
                <div style={{ fontSize: "12px", color: "#8a99ad", marginTop: "6px", lineHeight: "1.4" }}>
                    ℹ️ {helpText}
                </div>
            )}
            {dimensionInfo && (
                <div
                    style={{
                        fontSize: "12px",
                        color: "#28c76f",
                        backgroundColor: "rgba(40, 199, 111, 0.12)",
                        border: "1px solid rgba(40, 199, 111, 0.3)",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        marginTop: "6px",
                        fontWeight: "500",
                    }}
                >
                    ✓ {dimensionInfo} (Valid Landscape)
                </div>
            )}
            {dimensionError && (
                <div
                    style={{
                        fontSize: "12px",
                        color: "#ff4d4f",
                        backgroundColor: "rgba(255, 77, 79, 0.12)",
                        border: "1px solid rgba(255, 77, 79, 0.3)",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        marginTop: "6px",
                        lineHeight: "1.4",
                    }}
                >
                    ⚠️ {dimensionError}
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
